import { Grid, Typography } from '@material-ui/core';
import { Cancel } from '@material-ui/icons';
import { Option } from 'common/genericOptions';
import { useGenericStyles } from 'common/genericStyles';
import {
  CancelamentoQuantidadesRequest,
  FuncaoParticipante,
  InstrumentoFinanceiroApiResponse,
  RegistroStatus,
  TipoInteroperabilidade,
} from 'common/genericTypes';
import FinishedSign from 'components/finishedSign';
import { Button, Dropdown, LgSpace, NumberField, Section, SmSpace, TextField } from 'm2m.components';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { addCpfCnpjMask } from 'utils/mask';
import { formatNumber } from 'utils/number';
import { isNil } from 'utils/validation';
import InstrumentoAutocomplete from 'components/operacoes/instrumentoAutocomplete';
import { getQuantityType } from 'utils/instrumentoFinanceiroUtils';

const CancelamentoQuantidades: React.FC = () => {
  const dispatch = useDispatch();

  const getInstrumentosApi = useSelector((state: RootState) => state.instrumentoApi.getInstrumentosResume);
  const getInstrumentoDetalhes = useSelector((state: RootState) => state.instrumentoApi.getInstrumentoDetalhes);
  const getInvestidoresApi = useSelector((state: RootState) => state.investidorApi.getInvestidores);
  const getParticipantes = useSelector((state: RootState) => state.participanteApi.getParticipantesResume);
  const postCancelarQuantidades = useSelector((state: RootState) => state.operacaoApi.postCancelarQuantidades);
  const getPosicoes = useSelector((state: RootState) => state.investidorApi.getPosicoes);

  const classes = useGenericStyles();
  const [instrumentos, setInstrumentos] = useState<Array<InstrumentoFinanceiroApiResponse>>([]);
  const [investidorOptions, setInvestidorOptions] = useState<Array<Option>>();
  const [custodianteOptions, setCustodianteOptions] = useState<Array<Option>>();
  const [quantidadeMax, setQuantidadeMax] = useState<number>();

  const [instrumento, setInstrumento] = useState<InstrumentoFinanceiroApiResponse | null>(null);
  const [instrumentoInadimplido, setInstrumentoInadimplido] = useState<boolean>(false);
  const [investidorId, setInvestidorId] = useState<string>('');
  const [custodianteId, setCustodianteId] = useState<string>('');
  const [quantidade, setQuantidade] = useState<number>();
  const [justificativa, setJustificativa] = useState<string>('');
  const [finished, setFinished] = useState(false);

  const getInstrumentos = useCallback(async () => {
    try {
      const result = await getInstrumentosApi(dispatch, { registroStatus: [RegistroStatus.Aprovado] }, true);
      setInstrumentos(result);
    } catch { }
  }, [dispatch, getInstrumentosApi]);

  const getCustodiantes = useCallback(() => {
    getParticipantes(dispatch, { funcao: FuncaoParticipante.Custodiante, suspenso: false }, true)
      .then((result) => {
        const options = result?.map((custodiante) => ({
          value: custodiante.id!,
          description: custodiante.razaoSocial,
        }));
        setCustodianteOptions(options);
      })
      .catch();
  }, [dispatch, getParticipantes]);

  const getInvestidores = useCallback(
    async (custodianteId?: string) => {
      try {
        const result = await getInvestidoresApi(dispatch, { participanteId: custodianteId }, true);
        const options = result?.map((posicao) => ({
          value: posicao.id!,
          description: addCpfCnpjMask(posicao.cpfCnpj!),
        }));
        setInvestidorOptions(options);
      } catch { }
    },
    [dispatch, getInvestidoresApi]
  );

  const getPosicao = useCallback(
    async (investidorId: string, instrumentoFinanceiroId: string) => {
      try {
        const posicoes = await getPosicoes(dispatch, { instrumentoFinanceiroId, investidorId });
        if (posicoes.length === 0) {
          setQuantidadeMax(0);
        } else if (posicoes.length === 1) {
          const posicao = posicoes[0];
          setQuantidadeMax(posicao.saldoDisponivel);
        }
      } catch { }
    },
    [dispatch, getPosicoes]
  );

  useEffect(() => {
    getInstrumentos();
    getCustodiantes();
  }, [getInstrumentos, getCustodiantes]);

  useEffect(() => {
    if (instrumento) {
      getInstrumentoDetalhes(dispatch, instrumento.instrumentoFinanceiroGuid)
        .then((result) => setInstrumentoInadimplido(result?.hasPagamentosInadimplentes === true))
        .catch(() => { });
    } else {
      setInstrumentoInadimplido(false);
    }
  }, [instrumento, dispatch, getInstrumentoDetalhes]);

  const handleSubmit = async () => {
    try {
      if (instrumento && investidorId && custodianteId && quantidade && justificativa) {
        const cancelamentoRequest: CancelamentoQuantidadesRequest = {
          instrumentoFinanceiroId: instrumento.instrumentoFinanceiroGuid,
          investidorId,
          quantidade,
          custodianteId,
          justificativa,
        };

        await postCancelarQuantidades(dispatch, cancelamentoRequest, true);
        setFinished(true);
      }
    } catch (error) { }
  };

  useEffect(() => {
    if (instrumento && investidorId) getPosicao(investidorId, instrumento.instrumentoFinanceiroGuid);
  }, [investidorId, instrumento, getPosicao]);

  useEffect(() => {
    getInvestidores(custodianteId);
  }, [getInvestidores, custodianteId]);

  const formFulfilled = !!instrumento && investidorId && custodianteId && quantidade && justificativa;

  return (
    <div style={{ marginRight: 40 }}>
      <Typography variant="h1">{'Cancelar Quantidades'}</Typography>
      <LgSpace />
      {finished ? (
        <FinishedSign
          description={'Cancelamento de quantidades concluído com sucesso'}
          redirectRoute={'/operacoes/gerenciar'}
        />
      ) : (
        <>
          {instrumentoInadimplido && (
            <>
              <SmSpace />
              <div className={classes.errorToast}>
                <Cancel style={{ marginRight: 10 }} />
                <div style={{ display: 'flex', flex: 1 }}>
                  <Typography variant="body2">
                    {
                      'Este Instrumento está inadimplido, antes de continuar com o cancelamento solicite a ciência dos Custodiantes.'
                    }
                  </Typography>
                </div>
              </div>
            </>
          )}
          <Section title={'Detalhes da movimentação'} defaultGrid>
            <InstrumentoAutocomplete instrumentos={instrumentos} instrumento={instrumento} onChange={setInstrumento} />
            <Dropdown
              label="Custodiante"
              options={custodianteOptions ?? []}
              value={custodianteId ?? ''}
              onChange={setCustodianteId}
              required
            />
            <Dropdown
              label="CPF/CNPJ Investidor"
              disabled={!instrumento}
              options={investidorOptions ?? []}
              value={investidorId ?? ''}
              onChange={setInvestidorId}
              required
            />
            <NumberField
              label="Quantidade"
              value={quantidade}
              onChange={(value) => setQuantidade(value ?? 0)}
              required
              disabled={!investidorId}
              numberType={getQuantityType(instrumento?.tipoInstrumentoFinanceiro)}
              helperText={!isNil(quantidadeMax) ? `Quantidade disponível: ${formatNumber(quantidadeMax ?? 0, { minimumFractionDigits: 0, maximumFractionDigits: 8 })}` : ''}
            />
            <TextField
              label="Justificativa"
              value={justificativa}
              rows={4}
              onChange={(value) => setJustificativa(value)}
              multiline
              fullWidth
              required
            />
          </Section>
          <Grid container justify="center">
            <Grid item>
              <Button variant="contained" color="primary" disabled={!formFulfilled} onClick={handleSubmit}>
                {'Comandar Cancelamento'}
              </Button>
              <LgSpace />
            </Grid>
          </Grid>
        </>
      )}
    </div>
  );
};

export default CancelamentoQuantidades;
